"use client";

import * as React from "react";
import { useTranslations } from "@/i18n";
import { useModulePermission } from "@/features/auth/hooks/use-module-permission";
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types";
import { coreTextLimits } from "@ibada/shared-types/common";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { FinanceAccountType, FinanceCategoryScope, type FinanceAccountRecord } from "@/features/finance/model/finance-types";
import { getCurrencyPrefix } from "@/features/finance/lib/finance-utils";
import { collectFinanceGuardCodes } from "@/features/finance/lib/finance-guard-errors";
import { getApiFormErrorDetails } from "@/shared/lib/api-errors/form-api-errors";
import { useApplyApiFieldErrors, useApiFormBannerMessage } from "@/shared/lib/api-errors/api-form-errors";
import {
  buildFinanceAccountFormSchema,
  createDefaultFinanceAccountFormValues,
  type FinanceAccountFormInput,
  type FinanceAccountFormValues,
} from "@/features/finance/schemas/finance-configuration-forms";
import {
  useFinanceAccountTypesQuery,
  useFinanceCreateAccountMutation,
  useFinanceUpdateAccountMutation,
} from "@/features/finance/services/finance-query";
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import {
  MobileFormChoiceList,
  MobileFormError,
  MobileFormInputRow,
  MobileFormRow,
  MobileFormSection,
  MobileFormSheet,
  MobileFormTextArea,
} from "@/shared/components/mobile-form";
import { useFinanceSetupQuery } from "@/features/finance/services/finance-setup-api";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControlledField,
  HelperMessage,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  Select,
  Textarea,
} from "@ibada/ui";

type FinanceAccountFormMode = "create" | "edit";

export type FinanceAccountSavedResult = { mode: FinanceAccountFormMode; account: FinanceAccountRecord };

export type FinanceAccountFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: FinanceAccountFormMode;
  /** Required when `mode === "edit"`. */
  record?: FinanceAccountRecord | null;
  context: "branch" | "parish";
  workspaceId: string | null;
  branchId?: string | null;
  /** Department ownership (department finance). Scopes the created account to the department. */
  departmentId?: string | null;
  /** Department display name, shown as the scope badge for department accounts. */
  departmentName?: string | null;
  /** Overrides the scope derived from `context` (branch → Branch, parish → Parish). */
  scope?: FinanceCategoryScope;
  /** Fires with the created/updated account after a successful save, before the dialog closes. */
  onSaved?: (result: FinanceAccountSavedResult) => void;
};

/** The value set, ordered the way a treasurer thinks about it: the bank, the cash box, the phone. */
const ACCOUNT_TYPE_OPTIONS = [
  FinanceAccountType.Bank,
  FinanceAccountType.Cash,
  FinanceAccountType.MobileMoney,
] as const;

function mapAccountToFormValues(record: FinanceAccountRecord | null): FinanceAccountFormInput {
  if (!record) {
    return createDefaultFinanceAccountFormValues();
  }

  return {
    name: record.name,
    description: record.description ?? "",
    accountNumberLast4: record.accountNumberLast4 ?? "",
    type: record.type,
    openingBalance: record.openingBalance != null ? String(record.openingBalance) : "",
  };
}

function toFinanceAccountPayload(values: FinanceAccountFormInput) {
  const normalizeOptional = (value: string) => {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  return {
    name: values.name.trim(),
    description: normalizeOptional(values.description),
    accountNumberLast4: normalizeOptional(values.accountNumberLast4 ?? ""),
    type: values.type as FinanceAccountType,
  };
}

/**
 * The finance account create/edit dialog, extracted from the accounts settings tab so it can be reused
 * (e.g. from the income category dialog's "Add an account" affordance). Fully controlled; no trigger.
 */
export function FinanceAccountFormDialog({
  open,
  onOpenChange,
  mode,
  record = null,
  context,
  workspaceId,
  branchId = null,
  departmentId = null,
  scope,
  onSaved,
}: FinanceAccountFormDialogProps) {
  const t = useTranslations("churchConfig.page.financeAccounts");
  const { canEdit } = useModulePermission(WorkspaceAccessModule.FINANCE);
  const applyFieldErrors = useApplyApiFieldErrors<FinanceAccountFormInput>("finance");
  const formBannerMessage = useApiFormBannerMessage("finance");
  // The account's opening balance is money, so its currency (and scale) must be THIS account's branch currency.
  // Resolve it from the account's own record when editing, else authoritatively from the branch we're creating
  // the account under (its finance setup) — NOT `useBranchCurrency()`, which guesses the globally-active branch
  // by slug and picks the wrong one when slugs collide across branches/workspaces. `useBranchCurrency` stays only
  // as a last-resort fallback while the setup query is loading.
  const branchCurrencyFallback = useBranchCurrency();
  const branchSetup = useFinanceSetupQuery(workspaceId, branchId ?? null);
  const openingBalanceCurrency =
    record?.currency ?? branchSetup.data?.baseCurrency ?? branchCurrencyFallback;
  const schema = React.useMemo(
    () => buildFinanceAccountFormSchema((key, values) => t(key as never, values as never), openingBalanceCurrency),
    [openingBalanceCurrency, t],
  );

  const form = useForm<FinanceAccountFormValues, undefined, FinanceAccountFormInput>({
    resolver: zodResolver(schema),
    defaultValues: createDefaultFinanceAccountFormValues(),
  });

  const createAccountMutation = useFinanceCreateAccountMutation();
  const updateAccountMutation = useFinanceUpdateAccountMutation();
  // The "held as" options come from the backend's live FinanceAccountType enum, so the picker can never drift
  // from what the API accepts. The static enum is only a fallback while the request is in flight.
  const accountTypesQuery = useFinanceAccountTypesQuery();
  const accountTypeOptions =
    accountTypesQuery.data && accountTypesQuery.data.length > 0
      ? accountTypesQuery.data
      : (ACCOUNT_TYPE_OPTIONS as readonly string[]);
  const isPending = createAccountMutation.isPending || updateAccountMutation.isPending;

  const editRecord = mode === "edit" ? record : null;
  const isLockedForEdit = Boolean(editRecord && !editRecord.canEditFinancialDetails);
  // Opening balance (IBA-71) locks once the account has posted transactions — independent of financial-detail lock.
  const openingBalanceLocked = Boolean(editRecord?.openingBalanceLocked);
  const isEditUnchanged = Boolean(mode === "edit" && !form.formState.isDirty);

  // Seed the form when the dialog opens (create → defaults, edit → the record), and clear stale errors.
  React.useEffect(() => {
    if (!open) return;
    form.reset(mapAccountToFormValues(mode === "edit" ? record : null));
    form.clearErrors();
  }, [open, mode, record, form]);

  const resolvedScope = scope ?? (context === "branch" ? FinanceCategoryScope.Branch : FinanceCategoryScope.Parish);

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!canEdit) return;
    if (!workspaceId) {
      form.setError("root", { message: t("validation.workspaceRequired") });
      return;
    }
    if (context === "branch" && !branchId) {
      form.setError("root", { message: t("validation.branchRequired") });
      return;
    }

    form.clearErrors("root");

    try {
      const payload = toFinanceAccountPayload(values);

      // Opening balance (IBA-71): only send it when editable — create mode, or an unlocked account.
      const openingEditable = mode === "create" || Boolean(record && !record.openingBalanceLocked);
      // The schema already normalized it to a canonical decimal string (or `""` for "not stated"), so it goes to
      // the API untouched (negatives allowed — an overdrawn account is a real balance, IBA-120).
      const openingBalance = values.openingBalance;
      const openingPart = openingEditable && openingBalance !== "" ? { openingBalance } : {};

      if (mode === "edit" && record) {
        const updated = await updateAccountMutation.mutateAsync({
          id: record.id,
          patch: { ...payload, ...openingPart },
        });
        toast.success(t("toasts.updated"));
        onSaved?.({ mode: "edit", account: updated });
      } else {
        const created = await createAccountMutation.mutateAsync({
          workspaceId,
          branchId: branchId ?? undefined,
          departmentId: departmentId ?? undefined,
          scope: resolvedScope,
          optimisticCurrency: openingBalanceCurrency,
          ...payload,
          ...openingPart,
        });
        toast.success(t("toasts.created"));
        onSaved?.({ mode: "create", account: created });
      }

      onOpenChange(false);
    } catch (error) {
      applyFieldErrors(form.setError, error);

      const codes = collectFinanceGuardCodes(error);
      if (codes.includes("ACCOUNT_LOCKED_HAS_TRANSACTIONS")) {
        form.setError("root", { message: t("toasts.accountLockedError") });
        return;
      }

      const details = getApiFormErrorDetails(error);
      if (details.formErrors.length > 0) {
        form.setError("root", { message: t("toasts.saveError") });
        return;
      }

      form.setError("root", { message: formBannerMessage(error) });
    }
  });

  const isMobile = useIsMobile();

  if (!canEdit) return null;

  // Phone: the app's edit sheet with grouped rows. "Held as" is a check list in place (a picker would stack a
  // second sheet), the currency a read-only row beside the opening balance, the create warning a note at the end.
  if (isMobile) {
    const values = form.watch();
    const errors = form.formState.errors;
    const set = (name: keyof FinanceAccountFormInput, value: string) =>
      form.setValue(name, value as never, { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={onOpenChange}
        title={mode === "edit" ? t("dialog.editTitle") : t("dialog.createTitle")}
        note={mode === "create" ? t("hints.createWarning") : isLockedForEdit ? t("hints.lockedDetails") : undefined}
        cancelLabel={t("actions.cancel")}
        saveLabel={mode === "edit" ? t("actions.save") : t("actions.create")}
        onSave={() => void handleSubmit()}
        saving={isPending}
        saveDisabled={isEditUnchanged}
      >
        {errors.root?.message ? <HelperMessage tone='danger'>{errors.root.message}</HelperMessage> : null}

        <div>
          <MobileFormSection>
            <MobileFormInputRow
              label={t("fields.name")}
              value={values.name ?? ""}
              onChange={(event) => set("name", event.target.value)}
              placeholder={t("fields.namePlaceholder")}
              maxLength={coreTextLimits.financeAccountName}
              invalid={Boolean(errors.name)}
            />
            <MobileFormInputRow
              label={t("fields.accountNumberLast4Short")}
              value={values.accountNumberLast4 ?? ""}
              onChange={(event) => set("accountNumberLast4", event.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder={t("fields.accountNumberLast4Placeholder")}
              inputMode='numeric'
              maxLength={4}
            />
          </MobileFormSection>
          <MobileFormError message={errors.name?.message ?? errors.accountNumberLast4?.message} />
        </div>

        <MobileFormSection title={t("fields.type")}>
          <MobileFormChoiceList
            options={accountTypeOptions.map((option) => ({ value: option, label: t(`accountTypes.${option}`) }))}
            value={values.type}
            onChange={(next) => set("type", next)}
          />
        </MobileFormSection>

        <div>
          <MobileFormSection footer={openingBalanceLocked ? t("fields.currencyHint") : t("fields.openingBalanceHint")}>
            {!openingBalanceLocked ? (
              <MobileFormInputRow
                label={t("fields.openingBalanceShort")}
                value={values.openingBalance ?? ""}
                // Not the decimal pad: an overdrawn account is a real, negative balance (IBA-120) and needs "-".
                onChange={(event) => set("openingBalance", event.target.value.replace(",", ".").replace(/[^\d.-]/g, ""))}
                placeholder={`0 ${getCurrencyPrefix(openingBalanceCurrency)}`}
                inputMode='text'
                invalid={Boolean(errors.openingBalance)}
              />
            ) : null}
            <MobileFormRow label={t("fields.currency")} value={`${openingBalanceCurrency} (${getCurrencyPrefix(openingBalanceCurrency)})`} />
          </MobileFormSection>
          <MobileFormError message={errors.openingBalance?.message} />
        </div>

        <MobileFormSection title={t("fields.hint")}>
          <MobileFormTextArea
            value={values.description ?? ""}
            onChange={(event) => set("description", event.target.value)}
            placeholder={t("fields.hintPlaceholder")}
            maxLength={coreTextLimits.financeDescription}
            aria-label={t("fields.hint")}
          />
        </MobileFormSection>
      </MobileFormSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid='settings-accounts-dialog'>
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? t("dialog.editTitle") : t("dialog.createTitle")}</DialogTitle>
          <DialogDescription>{t("dialog.description")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className='space-y-4' onSubmit={handleSubmit}>
            {mode === "create" ? <HelperMessage tone='warning'>{t("hints.createWarning")}</HelperMessage> : null}
            {isLockedForEdit ? <HelperMessage tone='warning'>{t("hints.lockedDetails")}</HelperMessage> : null}
            {form.formState.errors.root?.message ? (
              <HelperMessage tone='danger'>{form.formState.errors.root.message}</HelperMessage>
            ) : null}

            <FormControlledField
              control={form.control}
              name='name'
              label={t("fields.name")}
              required
              renderInput={({ field }) => (
                <Input
                  id='finance-account-name'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fields.namePlaceholder")}
                  maxLength={coreTextLimits.financeAccountName}
                  showCount
                />
              )}
            />

            <FormControlledField
              control={form.control}
              name='type'
              label={t("fields.type")}
              renderInput={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={accountTypeOptions.map((option) => ({
                    value: option,
                    label: t(`accountTypes.${option}`),
                  }))}
                />
              )}
            />

            {/* Optional last-4 reference (display aid) — helps a treasurer tell similarly-named accounts apart.
                Not an identifier; never used for matching. */}
            <FormControlledField
              control={form.control}
              name='accountNumberLast4'
              label={t("fields.accountNumberLast4")}
              renderInput={({ field }) => (
                <Input
                  id='finance-account-number-last4'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fields.accountNumberLast4Placeholder")}
                  inputMode='numeric'
                  maxLength={4}
                />
              )}
            />

            {/* Currency is read-only: it is the branch's base currency (Ibada is single-currency per branch for
                now) and the backend fixes it — the field is here so the operator can see which currency the
                account holds, not to choose one. */}
            <div className='space-y-1.5'>
              <Label htmlFor='finance-account-currency'>{t("fields.currency")}</Label>
              <InputGroup>
                <InputGroupAddon align='inline-start'>
                  <InputGroupText>{getCurrencyPrefix(openingBalanceCurrency)}</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput
                  id='finance-account-currency'
                  value={openingBalanceCurrency}
                  readOnly
                  disabled
                  aria-readonly='true'
                />
              </InputGroup>
              <HelperMessage tone='info' size='sm'>
                {t("fields.currencyHint")}
              </HelperMessage>
            </div>

            {/* Once the account has POSTED transactions the opening balance can never change (the backend rejects
                it with ACCOUNT_LOCKED_HAS_TRANSACTIONS), so the field is not just disabled — it is removed. An
                account with only an opening-balance row and no posts keeps the field, editable, per IBA-71. */}
            {!openingBalanceLocked ? (
              <>
                <FormControlledField
                  control={form.control}
                  name='openingBalance'
                  label={t("fields.openingBalance")}
                  renderInput={({ field }) => (
                    <InputGroup>
                      <InputGroupAddon align='inline-start'>
                        <InputGroupText>{getCurrencyPrefix(openingBalanceCurrency)}</InputGroupText>
                      </InputGroupAddon>
                      <InputGroupInput
                        id='finance-account-opening-balance'
                        type='number'
                        // Deliberately no `min`: an overdrawn account is a real balance (IBA-120), and the field
                        // must be able to say so.
                        step='any'
                        inputMode='decimal'
                        value={field.value}
                        onChange={field.onChange}
                        placeholder='0'
                      />
                    </InputGroup>
                  )}
                />
                <HelperMessage tone='info' size='sm'>
                  {t("fields.openingBalanceHint")}
                </HelperMessage>
              </>
            ) : null}

            <FormControlledField
              control={form.control}
              name='description'
              label={t("fields.hint")}
              renderInput={({ field }) => (
                <Textarea
                  id='finance-account-description'
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("fields.hintPlaceholder")}
                  maxLength={coreTextLimits.financeDescription}
                  showCount
                />
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='secondary'
                onClick={() => onOpenChange(false)}
                disabled={isPending}
                data-testid='settings-accounts-dialog-cancel-button'
              >
                {t("actions.cancel")}
              </Button>
              <Button
                type='submit'
                disabled={isPending || isEditUnchanged}
                data-testid='settings-accounts-dialog-submit-button'
              >
                {isPending ? t("actions.saving") : mode === "edit" ? t("actions.save") : t("actions.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
